import React, { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Badge } from '@shared/components/ui/badge';
import { Switch } from '@shared/components/ui/switch';
import {
  QrCode,
  Camera,
  CheckCircle2,
  XCircle,
  User,
  Building2,
  AlertCircle,
  Clock,
  Maximize,
  Minimize,
  CameraOff,
  FolderKanban,
  FileText,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';
import { validatePassAtKiosk, kioskCheckin, kioskScanBff } from '@shared/services/passService';
import { ENV } from '@shared/config/env';
import {
  kioskLogin,
  getKioskToken,
  setKioskSession,
  clearKioskSession,
  getKioskDepotId,
  getKioskDeviceName,
  isKioskSessionExpired,
} from '@shared/services/kioskAuthService';

/**
 * Kiosk Scanner Interface
 * iPad-optimized fullscreen QR scanner for check-in/check-out
 */
const KioskScanner = () => {
  const { user } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [kioskMode, setKioskMode] = useState('check-in'); // 'check-in' or 'check-out'
  const [scannedToken, setScannedToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [briefUpdatePending, setBriefUpdatePending] = useState(null); // { token, briefs, personName }
  const [briefAcknowledging, setBriefAcknowledging] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanStateRef = useRef({ rafId: null, lastValue: null, lastTime: 0 });
  const [kioskPin, setKioskPin] = useState('');
  const [kioskDepotId, setKioskDepotId] = useState('');
  const [kioskDeviceName, setKioskDeviceName] = useState('');
  const [kioskAuthLoading, setKioskAuthLoading] = useState(false);
  const [fitnessAttestation, setFitnessAttestation] = useState({
    no_impairment: false,
    ppe_acknowledged: false,
  });
  const { toast } = useToast();

  const playBeep = () => {
    try {
      const AudioContextRef = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextRef) return;
      const audioContext = new AudioContextRef();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.value = 0.04;

      oscillator.connect(gain);
      gain.connect(audioContext.destination);

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.12);

      oscillator.onended = () => {
        audioContext.close();
      };
    } catch (_error) {
      // no-op if audio is not available
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    const diffMs = new Date(dateStr).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const storedDepotId = getKioskDepotId();
    const storedDeviceName = getKioskDeviceName();

    if (storedDepotId) {
      setKioskDepotId(storedDepotId);
    } else if (user?.depot_id) {
      setKioskDepotId(user.depot_id);
    }

    if (storedDeviceName) {
      setKioskDeviceName(storedDeviceName);
    }

    if (isKioskSessionExpired()) {
      clearKioskSession();
    }
  }, [user?.depot_id]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  useEffect(() => {
    if (!cameraActive || !stream || !videoRef.current) return;

    const video = videoRef.current;
    video.srcObject = stream;
    video.muted = true;

    const handleLoaded = async () => {
      try {
        await video.play();
      } catch (playError) {
        console.warn('Camera play blocked:', playError);
      }
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    return () => video.removeEventListener('loadedmetadata', handleLoaded);
  }, [cameraActive, stream]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setCameraActive(true);
      setStream(mediaStream);
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        title: 'Camera Error',
        description: 'Could not access camera. Please check permissions.',
        variant: 'destructive',
      });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const handleQRScan = async (tokenToProcess = null) => {
    // If tokenToProcess is an event object, ignore it and use scannedToken
    const token = (typeof tokenToProcess === 'string') ? tokenToProcess : scannedToken;
    
    if (!token || typeof token !== 'string' || !token.trim()) {
      setErrorMessage('Please enter or scan a QR code');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setProcessing(true);
    setShowError(false);
    setShowSuccess(false);
    stopCamera(); // Stop camera when processing

    try {
      if (kioskMode === 'check-in') {
        await handleCheckIn(token.trim());
      } else {
        await handleCheckOut(token.trim());
      }
    } catch (error) {
      console.error('Scan error:', error);
      setErrorMessage(error.message || 'Failed to process QR code');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setProcessing(false);
      setScannedToken('');
    }
  };

  useEffect(() => {
    if (!cameraActive || !stream || !videoRef.current || !canvasRef.current) {
      if (scanStateRef.current.rafId) {
        cancelAnimationFrame(scanStateRef.current.rafId);
        scanStateRef.current.rafId = null;
      }
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context) {
      return;
    }

    let cancelled = false;

    const scanFrame = () => {
      if (cancelled) return;

      if (processing || !cameraActive) {
        scanStateRef.current.rafId = requestAnimationFrame(scanFrame);
        return;
      }

      if (video.readyState >= 2) {
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width && height) {
          if (canvas.width !== width) canvas.width = width;
          if (canvas.height !== height) canvas.height = height;

          context.drawImage(video, 0, 0, width, height);
          const imageData = context.getImageData(0, 0, width, height);
          const result = jsQR(imageData.data, width, height, { inversionAttempts: 'attemptBoth' });

          if (result?.data) {
            const now = Date.now();
            const isDuplicate = scanStateRef.current.lastValue === result.data;
            const isThrottled = now - scanStateRef.current.lastTime < 1500;

            if (!isDuplicate || !isThrottled) {
              scanStateRef.current.lastValue = result.data;
              scanStateRef.current.lastTime = now;
              playBeep();
              handleQRScan(result.data);
              return;
            }
          }
        }
      }

      scanStateRef.current.rafId = requestAnimationFrame(scanFrame);
    };

    scanStateRef.current.rafId = requestAnimationFrame(scanFrame);

    return () => {
      cancelled = true;
      if (scanStateRef.current.rafId) {
        cancelAnimationFrame(scanStateRef.current.rafId);
        scanStateRef.current.rafId = null;
      }
    };
  }, [cameraActive, stream, processing, handleQRScan]);

  const handleCheckIn = async (token) => {
    const useBff = ENV.FEATURES.USE_KIOSK_BFF;
    const depotId = useBff ? kioskDepotId : user?.depot_id;

    if (!depotId) {
      throw new Error('No depot selected');
    }

    // Validate fitness attestation first
    if (!fitnessAttestation.no_impairment || !fitnessAttestation.ppe_acknowledged) {
      throw new Error('Please confirm fitness attestation');
    }

    if (useBff) {
      const result = await kioskScanBff(token, {
        action: 'check_in',
        siteId: depotId,
        kioskId: kioskDeviceName || (user?.id ? `iPad-${user.id.substring(0, 8)}` : null),
      });

      if (!result?.ok) {
        throw new Error(result?.message || 'Check-in denied');
      }

      setCurrentUser({
        name: result?.person?.name || 'Visitor',
        type: result?.person?.type,
        company: result?.person?.company,
        projectName: result?.person?.project_name,
        time: new Date().toLocaleTimeString(),
        status: 'active',
        fitnessConfirmed: fitnessAttestation.no_impairment && fitnessAttestation.ppe_acknowledged,
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setCurrentUser(null);
        setFitnessAttestation({ no_impairment: false, ppe_acknowledged: false });
      }, 5000);

      return;
    }

    // Validate pass via RPC
    const passValidation = await validatePassAtKiosk(token);

    if (passValidation.valid) {
      // Valid pass - check in via RPC
      const checkinResult = await kioskCheckin(token);

      if (!checkinResult.success) {
        throw new Error(checkinResult.reason || 'Check-in failed');
      }

      // Show success screen
      setCurrentUser({
        name: checkinResult.person_name,
        type: checkinResult.person_type,
        company: passValidation.person_company,
        projectName: checkinResult.project_name || passValidation.project_name,
        time: new Date().toLocaleTimeString(),
        status: 'active',
        validFrom: passValidation.valid_from,
        validUntil: passValidation.valid_until,
        fitnessConfirmed: fitnessAttestation.no_impairment && fitnessAttestation.ppe_acknowledged,
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setCurrentUser(null);
        setFitnessAttestation({ no_impairment: false, ppe_acknowledged: false });
      }, 5000);

      return;
    }

    // Brief update required — show on-screen acknowledgement
    if (passValidation.reason === 'BRIEF_UPDATE_REQUIRED') {
      setBriefUpdatePending({
        token,
        briefs: passValidation.briefs || [],
        personName: passValidation.person_name || '',
      });
      return;
    }

    // Any other validation failure — surface the reason
    throw new Error(passValidation.reason || 'Invalid pass');
  };

  const handleCheckOut = async (token) => {
    const useBff = ENV.FEATURES.USE_KIOSK_BFF;
    const depotId = useBff ? kioskDepotId : user?.depot_id;

    if (!depotId) {
      throw new Error('No depot selected');
    }

    if (useBff) {
      const result = await kioskScanBff(token, {
        action: 'check_out',
        siteId: depotId,
        kioskId: kioskDeviceName || (user?.id ? `iPad-${user.id.substring(0, 8)}` : null),
      });

      if (!result?.ok) {
        throw new Error(result?.message || 'Check-out denied');
      }

      setCurrentUser({
        name: result?.person?.name || 'Visitor',
        type: result?.person?.type,
        company: result?.person?.company,
        time: new Date().toLocaleTimeString(),
        status: 'checked_out',
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setCurrentUser(null);
      }, 5000);

      return;
    }

    // 1. Look up the pass to get the pass_id
    const { data: pass, error: passError } = await supabase
      .from('passes')
      .select('id, person_name, person_type, person_company')
      .eq('pass_token', token)
      .single();

    if (passError || !pass) {
      throw new Error('Invalid QR code — pass not found');
    }

    // 2. Find the person's active session by pass_id
    const { data: sessions, error: fetchError } = await supabase
      .from('site_sessions')
      .select('*')
      .eq('pass_id', pass.id)
      .is('checked_out_at', null)
      .order('checked_in_at', { ascending: false })
      .limit(1);

    if (fetchError) throw fetchError;

    if (!sessions || sessions.length === 0) {
      throw new Error('No active check-in found. You may have already checked out.');
    }

    const session = sessions[0];

    // 3. Update session with check-out time
    const { error: updateError } = await supabase
      .from('site_sessions')
      .update({
        checked_out_at: new Date().toISOString(),
        checked_out_kiosk: user?.id ? `iPad-${user.id.substring(0, 8)}` : null,
      })
      .eq('id', session.id);

    if (updateError) throw updateError;

    // Show success screen
    setCurrentUser({
      name: pass.person_name || session.person_name,
      type: pass.person_type || session.person_type,
      company: pass.person_company || session.person_company,
      time: new Date().toLocaleTimeString(),
      status: 'checked_out',
    });

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setCurrentUser(null);
    }, 5000);
  };

  const handleCameraClick = () => {
    if (cameraActive) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  const handleAcknowledgeBrief = async () => {
    if (!briefUpdatePending) return;
    setBriefAcknowledging(true);
    try {
      const { data, error } = await supabase.rpc('kiosk_acknowledge_brief_update', {
        p_pass_token: briefUpdatePending.token,
      });
      if (error) throw error;
      if (!data?.success) throw new Error('Acknowledgement failed');
      const token = briefUpdatePending.token;
      setBriefUpdatePending(null);
      setProcessing(true);
      await handleCheckIn(token);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to acknowledge brief update');
      setShowError(true);
      setBriefUpdatePending(null);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setBriefAcknowledging(false);
      setProcessing(false);
    }
  };

  const handleKioskLogin = async () => {
    if (!kioskDepotId || !kioskPin) {
      toast({
        title: 'Missing details',
        description: 'Depot ID and PIN are required.',
        variant: 'destructive',
      });
      return;
    }

    setKioskAuthLoading(true);
    try {
      const result = await kioskLogin({
        pin: kioskPin,
        depot_id: kioskDepotId,
        device_name: kioskDeviceName || null,
      });

      if (!result?.ok || !result?.kiosk_token) {
        throw new Error(result?.error?.message || 'Kiosk login failed');
      }

      setKioskSession({
        kiosk_token: result.kiosk_token,
        expires_at: result.expires_at,
        depot_id: result.depot_id,
        device_name: kioskDeviceName || null,
      });

      setKioskPin('');
      toast({
        title: 'Kiosk authenticated',
        description: 'Device is ready for scanning.',
      });
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error?.message || 'Invalid PIN or depot.',
        variant: 'destructive',
      });
    } finally {
      setKioskAuthLoading(false);
    }
  };

  const handleKioskLogout = () => {
    clearKioskSession();
    toast({
      title: 'Kiosk signed out',
      description: 'Enter PIN to continue scanning.',
    });
  };

  const useBff = import.meta.env.VITE_USE_KIOSK_BFF === 'true';
  const kioskToken = getKioskToken();

  if (useBff && !kioskToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md p-6 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold">Kiosk Login</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Enter depot PIN to enable kiosk mode.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Depot ID</Label>
              <Input
                value={kioskDepotId}
                onChange={(event) => setKioskDepotId(event.target.value)}
                placeholder="Depot ID"
              />
            </div>

            <div>
              <Label>Device name (optional)</Label>
              <Input
                value={kioskDeviceName}
                onChange={(event) => setKioskDeviceName(event.target.value)}
                placeholder="Front Gate iPad"
              />
            </div>

            <div>
              <Label>PIN</Label>
              <Input
                value={kioskPin}
                onChange={(event) => setKioskPin(event.target.value)}
                placeholder="••••"
                type="password"
                inputMode="numeric"
              />
            </div>
          </div>

          <Button className="w-full" onClick={handleKioskLogin} disabled={kioskAuthLoading}>
            {kioskAuthLoading ? 'Signing in…' : 'Login'}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className={`${isFullscreen ? 'h-screen' : 'h-full'} bg-gradient-to-br from-gray-50 to-gray-100 relative`}
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.85)), url('/assets/ATC-PHOTO-71.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="container max-w-4xl mx-auto p-6 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-primary rounded-lg flex items-center justify-center">
              <QrCode className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Depot Check-In Kiosk</h1>
              <p className="text-sm text-gray-600">Scan your QR code to enter or exit</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {useBff && (
              <Button variant="outline" onClick={handleKioskLogout} className="bg-white border-gray-200 hover:bg-gray-50">
                Logout / Switch Depot
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="bg-white border-gray-200 hover:bg-gray-50"
            >
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setKioskMode('check-in')}
            className={`flex-1 h-16 text-lg text-white ${
              kioskMode === 'check-in'
                ? 'bg-green-600 hover:bg-green-700 ring-4 ring-green-200'
                : 'bg-green-500 hover:bg-green-600 opacity-60'
            }`}
          >
            <CheckCircle2 className="h-6 w-6 mr-2" />
            Check In
          </Button>
          <Button
            onClick={() => setKioskMode('check-out')}
            className={`flex-1 h-16 text-lg text-white ${
              kioskMode === 'check-out'
                ? 'bg-red-600 hover:bg-red-700 ring-4 ring-red-200'
                : 'bg-red-500 hover:bg-red-600 opacity-60'
            }`}
          >
            <XCircle className="h-6 w-6 mr-2" />
            Check Out
          </Button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center space-y-6">
          {!showSuccess && !showError && !processing && !briefUpdatePending && (
            <>
              {/* Scanner Area */}
              <Card className="bg-white border-2 border-gray-300 shadow-md">
                <CardContent className="pt-6">
                  <div className="text-center space-y-6">
                    <div className="mx-auto w-64 h-64 bg-gray-50 border-2 border-gray-300 rounded-2xl flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors overflow-hidden relative"
                         onClick={handleCameraClick}>
                      {cameraActive ? (
                        <>
                          <video 
                            ref={videoRef}
                            autoPlay 
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                          />
                          <canvas ref={canvasRef} className="hidden" />
                          <div className="absolute bottom-2 left-0 right-0 text-center">
                            <p className="text-xs text-white bg-black/50 px-2 py-1 rounded inline-block">
                              Position QR code in camera view
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="text-center">
                          <Camera className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                          <p className="text-sm text-gray-700">Tap to activate camera</p>
                          <p className="text-xs text-gray-500 mt-2">Scan QR code with camera</p>
                        </div>
                      )}
                    </div>

                    {cameraActive && (
                      <Button
                        onClick={stopCamera}
                        variant="outline"
                        className="bg-white border-gray-200 hover:bg-gray-50"
                      >
                        <CameraOff className="h-4 w-4 mr-2" />
                        Stop Camera
                      </Button>
                    )}

                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <span className="text-sm text-gray-600">OR</span>
                      <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    <div className="space-y-3">
                      <Input
                        value={scannedToken}
                        onChange={(e) => setScannedToken(e.target.value)}
                        placeholder="Enter QR code manually..."
                        className="h-14 text-lg bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                        onKeyPress={(e) => e.key === 'Enter' && handleQRScan()}
                      />
                      <Button
                        onClick={handleQRScan}
                        disabled={!scannedToken.trim() || processing}
                        className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
                      >
                        {processing ? 'Processing...' : `${kioskMode === 'check-in' ? 'Check In' : 'Check Out'}`}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Fitness Attestation (Check-In Only) */}
              {kioskMode === 'check-in' && (
                <Card className="bg-white border-2 border-gray-300 shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Before Entering Site
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                      <Switch
                        id="no_impairment"
                        checked={fitnessAttestation.no_impairment}
                        onCheckedChange={(checked) =>
                          setFitnessAttestation({ ...fitnessAttestation, no_impairment: checked })
                        }
                        className="border-2 border-gray-400 data-[state=checked]:border-green-600 scale-125"
                      />
                      <Label htmlFor="no_impairment" className="text-gray-900 cursor-pointer">
                        I confirm I am fit for work and not impaired by alcohol, drugs, or medication
                      </Label>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                      <Switch
                        id="ppe_acknowledged"
                        checked={fitnessAttestation.ppe_acknowledged}
                        onCheckedChange={(checked) =>
                          setFitnessAttestation({ ...fitnessAttestation, ppe_acknowledged: checked })
                        }
                        className="border-2 border-gray-400 data-[state=checked]:border-green-600 scale-125"
                      />
                      <Label htmlFor="ppe_acknowledged" className="text-gray-900 cursor-pointer">
                        I will wear required PPE at all times while on site
                      </Label>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Brief Acknowledgement Screen */}
          {briefUpdatePending && (
            <Card className="bg-amber-50 border-2 border-amber-400 shadow-md animate-in fade-in">
              <CardContent className="pt-6">
                <div className="text-center space-y-4 py-6">
                  <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                    <FileText className="h-10 w-10 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-amber-900 mb-1">Brief Update Required</h2>
                    {briefUpdatePending.personName && (
                      <p className="text-amber-800 font-medium">{briefUpdatePending.personName}</p>
                    )}
                    <p className="text-amber-700 text-sm mt-1">Please read the updated site brief below before entering.</p>
                  </div>
                  <div className="text-left space-y-3 max-h-64 overflow-y-auto">
                    {briefUpdatePending.briefs.map((brief, idx) => (
                      <div key={idx} className="bg-white border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {brief.brief_title}
                          <span className="text-xs text-amber-600 font-normal">v{brief.brief_version}</span>
                        </h3>
                        {brief.brief_content && (
                          <p className="text-gray-700 text-sm whitespace-pre-wrap">{brief.brief_content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 pt-2">
                    <Button
                      onClick={handleAcknowledgeBrief}
                      disabled={briefAcknowledging}
                      className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {briefAcknowledging ? 'Confirming...' : 'I have read and understood the brief'}
                    </Button>
                    <Button
                      onClick={() => setBriefUpdatePending(null)}
                      variant="outline"
                      disabled={briefAcknowledging}
                      className="w-full h-10 text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Screen */}
          {showSuccess && currentUser && (
            <Card className="bg-green-600 border-green-500 backdrop-blur animate-in fade-in">
              <CardContent className="pt-6">
                <div className="text-center space-y-6 py-12">
                  <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-16 w-16 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-2">
                      {kioskMode === 'check-in' ? 'Welcome!' : 'Goodbye!'}
                    </h2>
                    <p className="text-lg text-green-100">
                      {kioskMode === 'check-in' ? 'Successfully checked in' : 'Successfully checked out'}
                    </p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-6 inline-block">
                    <div className="flex items-center gap-2 text-white mb-2">
                      <User className="h-5 w-5" />
                      <span className="text-xl font-semibold">{currentUser.name}</span>
                    </div>
                    {currentUser.company && (
                      <div className="flex items-center gap-2 text-green-100 mb-2">
                        <Building2 className="h-4 w-4" />
                        <span>{currentUser.company}</span>
                      </div>
                    )}
                    {currentUser.projectName && (
                      <div className="flex items-center gap-2 text-green-100 mb-2">
                        <FolderKanban className="h-4 w-4" />
                        <span>{currentUser.projectName}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-green-100">
                      <Clock className="h-4 w-4" />
                      <span>{currentUser.time}</span>
                    </div>
                    {currentUser.status && (
                      <div className="flex items-center gap-2 text-green-100 mt-2">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Induction status: {currentUser.status}</span>
                      </div>
                    )}
                    {currentUser.validUntil && (
                      <div className="flex items-center gap-2 text-green-100 mt-2">
                        <Clock className="h-4 w-4" />
                        <span>Valid until {formatDate(currentUser.validUntil)} ({getDaysRemaining(currentUser.validUntil)} days left)</span>
                      </div>
                    )}
                    {currentUser.fitnessConfirmed && kioskMode === 'check-in' && (
                      <div className="flex items-center gap-2 text-green-100 mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <span>Fit-to-work confirmed</span>
                      </div>
                    )}
                    <Badge className="mt-3 bg-white text-green-600">
                      {currentUser.type}
                    </Badge>
                  </div>
                  {kioskMode === 'check-in' && (
                    <p className="text-sm text-green-100">
                      Please proceed to your designated work area
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Screen */}
          {showError && (
            <Card className="bg-red-600 border-red-500 backdrop-blur animate-in fade-in">
              <CardContent className="pt-6">
                <div className="text-center space-y-6 py-12">
                  <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center">
                    <XCircle className="h-16 w-16 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-2">Error</h2>
                    <p className="text-lg text-red-100">{errorMessage}</p>
                  </div>
                  <p className="text-sm text-red-100">
                    Please try again or contact site management
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>iPad Kiosk Station • For assistance, contact site management</p>
          <p className="mt-1">iOS Guided Access Mode: Hold Home button 3x to configure</p>
        </div>
      </div>
    </div>
  );
};

export default KioskScanner;
